import React, { useState } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { EqualMark, Eyebrow, StepNumber } from "@/components/brand";
import { Learn } from "@/components/Learn";

export const STUDIO_GUIDE_KEY = "equal_studio_guide_v1";

export const STUDIO_GUIDE_STEPS = [
  {
    rail: "Before you start",
    title: "What you'll need",
    body: "Pick a license type, enter the space, and Equal drafts a code-compliant plan, prices it, and packages it for your architect. Have your license type, address and rough dimensions ready — about 15 minutes.",
    more: "Templates are modeled on anonymized Long Beach equity applicant projects — names, addresses, dates and exact figures are withheld. Dimensions can be a best guess from the listing or a tape measure; you can adjust them at any time.",
    tip: "Name your project once (Save, left sidebar) and every change auto-saves. Overlay images are the one thing not saved.",
  },
  {
    rail: "Your space",
    title: "Step 1 — Describe your space",
    body: "Choose the use case, then enter the address and dimensions. Equal loads a starter layout modeled on an anonymized Long Beach project of the same type.",
    more: "Use cases include retail storefront, delivery-only, cultivation / manufacturing / distribution and microbusiness. The starter layout scales to your dimensions; you can reset to the template later.",
    tip: "Not sure of the dimensions? Best guess is fine — adjust later.",
  },
  {
    rail: "Floor plan",
    title: "Step 2 — Shape the floor plan",
    body: "Click a room to rename, resize, duplicate or delete it; drag it to move. Add rooms, clear the plan, or reset to the template — with one-click Undo.",
    more: "The Room Schedule lists every room and its square footage. Street frontage is on the left, loading and rear access on the right; solid outlines mark the limited-access rooms the City will inspect. You can overlay your own plan image and download the result as SVG or PNG.",
    tip: "Have the landlord's plan? Overlay the image and trace over it.",
  },
  {
    rail: "Long Beach check",
    title: "Step 3 — Run the Long Beach check",
    body: "Your plan is checked against LBMC Chapter 5.92 and bulletin IB-051 — the same items the City's plan checkers use. Green passes, amber needs your input, red must be fixed.",
    more: "Fill in the Site Inputs first (sensitive-use distances, parking, frontage) — several checks can't turn green without them. Every check cites its code section; click the citation to read the City's official text inside the Studio.",
    tip: "Fill in the Site Inputs first — several checks can't turn green without them.",
    links: [
      { label: "LBMC Chapter 5.92", href: "https://library.municode.com/ca/long_beach/codes/municipal_code?nodeId=TIT5REBUTRPR_CH5.92ADECABUAC" },
      { label: "LBMC Chapter 5.90 (security)", href: "https://library.municode.com/ca/long_beach/codes/municipal_code?nodeId=TIT5REBUTRPR_CH5.90MEMABU" },
      { label: "IB-051 bulletin (PDF)", href: "https://www.longbeach.gov/globalassets/lbcd/media-library/documents/building--safety/information-bulletins/ib-051" },
    ],
  },
  {
    rail: "Paint & finishes",
    title: "Step 4 — Choose paint and finishes",
    body: "Pick a scheme, then click any room to set its wall color, flooring, ceiling, lighting and fixtures.",
    more: "Wall area is computed from your plan (perimeter × ceiling height, minus doors and storefront glass, two coats). Flooring is priced per type and adjusts the budget's Finishes line. Every choice carries into the 2D preview, 3D view, AI renderings and the architect package's Finish Schedule.",
    tip: "Use 'Other details' for one-off touches — a plant wall, neon sign, live-edge counter.",
  },
  {
    rail: "See your space",
    title: "Step 5 — See your space",
    body: "Walk through your plan in 3D, record a fly-through video, and generate photoreal renderings of each room — all from your rooms, dimensions and finishes.",
    more: "Drag to orbit, scroll to zoom. Record walkthrough saves a WebM you can send to your architect or the City. Renderings are AI-generated from each room's size, use, paint, flooring, ceiling, lighting and fixtures; play them as a captioned slideshow video or download them individually.",
    tip: "Generate renderings once, then re-render only the rooms you change.",
  },
  {
    rail: "Build-out budget",
    title: "Step 6 — Price the build-out",
    body: "Priced between an anonymized Long Beach contractor bid and an owner-managed budget. Drag the slider to where you expect to land.",
    more: "Line items follow a contractor's typical division breakdown; toggle grow / lab equipment on or off. Pair it with Market Data: rent per square foot plus build-out per square foot is the real cost of the space.",
    tip: "Rent $/sf + build-out $/sf = the real cost of the space.",
  },
  {
    rail: "Vendors & prices",
    title: "Step 7 — Find vendors near your site",
    body: "Suppliers within driving distance of your address, with live prices where vendors publish them.",
    more: "Contractor supply, paint, electrical, plumbing and HVAC, grow equipment and security. Prices are a starting point for quotes — always get at least two bids on major items.",
    tip: "Always get two bids on major items.",
  },
  {
    rail: "Architect package",
    title: "Step 8 — Hand it to your architect",
    body: "One page your architect, contractor and the Equity Program can all read. Export as PDF, JSON or send it by email.",
    more: "The package includes floor plan, 3D snapshot, renderings, program summary, budget and a list of open items for the architect to resolve. Bring it to your next Schedule a Call session — it makes the conversation concrete.",
    tip: "Bring it to your next Schedule a Call session.",
  },
];

export function StudioGuideModal({ onClose }) {
  const [step, setStep] = useState(0);
  const last = step === STUDIO_GUIDE_STEPS.length - 1;
  const s = STUDIO_GUIDE_STEPS[step];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-ink/70" data-testid="studio-guide-modal">
      <div className="w-full max-w-3xl card-flat rounded-2xl overflow-hidden flex flex-col animate-rise-in">
        <div className="px-6 py-4 flex items-center justify-between border-b border-line">
          <div className="flex items-center gap-3">
            <EqualMark size={24} />
            <span className="text-ink font-bold tracking-tight">How the Design Studio works</span>
          </div>
          <button onClick={onClose} data-testid="studio-guide-close" className="text-mute hover:text-ink transition-colors" aria-label="Close guide">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="grid sm:grid-cols-[210px_1fr]">
          <aside className="bg-page border-b sm:border-b-0 sm:border-r border-line p-3 flex sm:flex-col gap-1 overflow-x-auto">
            {STUDIO_GUIDE_STEPS.map((t, i) => (
              <button key={t.rail} onClick={() => setStep(i)} data-testid={`studio-guide-rail-${i}`}
                className={`flex items-center gap-2.5 rounded-[10px] px-2.5 py-1.5 flex-none sm:flex-auto text-left transition-colors ${i === step ? "bg-mint text-ink" : "text-mute hover:text-ink"}`}>
                <StepNumber n={i === 0 ? "•" : i} active={i === step} size={20} />
                <span className="hidden sm:block text-xs font-medium leading-snug">{t.rail}</span>
              </button>
            ))}
          </aside>
          <div className="px-7 py-7 min-h-[300px] flex flex-col">
            <Eyebrow className="text-equal-dark mb-2">{step === 0 ? "Getting started" : `Step ${step} of ${STUDIO_GUIDE_STEPS.length - 1}`}</Eyebrow>
            <h3 className="text-2xl font-bold text-ink tracking-tight mb-3" data-testid="studio-guide-title">{s.title}</h3>
            <p className="text-sm text-ink-2 leading-relaxed">{s.body}</p>
            {s.more && <Learn className="mt-3" testId="studio-guide-more">{s.more}</Learn>}
            <div className="mt-4 rounded-[10px] bg-cream border border-line px-4 py-3 text-xs text-ink-2 leading-relaxed" data-testid="studio-guide-tip">
              <span className="font-semibold text-ink">Tip · </span>{s.tip}
            </div>
            {s.links && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="studio-guide-links">
                {s.links.map((l) => (
                  <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-line bg-white px-3 py-1 text-xs font-medium text-ink hover:border-equal hover:text-equal-dark transition-colors">
                    {l.label} <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
              </div>
            )}
            <div className="mt-auto pt-6 flex items-center justify-between">
              <div className="flex gap-1.5">
                {STUDIO_GUIDE_STEPS.map((_, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-[width,background-color] ${i === step ? "w-5 bg-equal" : "w-1.5 bg-line"}`} />
                ))}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} data-testid="studio-guide-back" className="btn-ghost h-9 px-3">
                    <ChevronLeft className="w-4 h-4" /> Back
                  </button>
                )}
                {last ? (
                  <button onClick={onClose} data-testid="studio-guide-done" className="btn-primary h-9 px-5">Start building</button>
                ) : (
                  <button onClick={() => setStep(step + 1)} data-testid="studio-guide-next" className="btn-primary h-9 px-5">
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
